/** prettier-ignore */
import {Image, Text, View} from '@react-pdf/renderer';
import React from 'react';
import {styles} from '../styles';

import sleipnerBackground from 'assets/pdf/sleipner-pdf-bg.png';
import {
	HighlightBox,
	Hr,
	LegendItem,
	MultiLineText,
	PageWithHeaderAndFooter,
	Row,
	RowItem,
	TextLine,
	TextLineWithValue,
	TextValuePill,
	ValueLarge,
	ValueTitle,
} from '../documentComponents';
import {baseFormatClean, formatCurrency} from 'components/ModelDetailsTable';
import rightArrow from 'assets/pdf/arrow-right-solid.png';

export const UtilizationPage = ({
	customer,
	showellUser,
	recommendedSleipner,
	calculatedValues,
	formValues,
	charts,
}) => {
	return (
		<PageWithHeaderAndFooter
			headerBackgroundImageUrl={sleipnerBackground}
			footerBackgroundImageUrl={sleipnerBackground}
			customer={customer}
			pageTitle="Production Benefits"
			showellUser={showellUser}
		>
			<View style={styles.layout.containerHorizontal}>
				<Row>
					<RowItem>
						<ValueTitle>Working Hours</ValueTitle>
						<TextLine>Hours worked with one excavator in</TextLine>
						<TextLineWithValue
							text="one year"
							value={formValues['annualOperatingHours(h)'] + ' h'}
						/>
					</RowItem>
					<RowItem styleProps={{marginLeft: '20px'}}>
						<ValueTitle>Time spent travelling annually</ValueTitle>
						<TextLineWithValue
							text="Total time spent travelling"
							value={
								calculatedValues['Annual time spent travelling without Sleipner (h)'] +
								' h'
							}
						/>
						<TextLineWithValue
							text="Distance covered"
							value={
								calculatedValues['Annual time spent travelling without Sleipner (h)'] *
									parseFloat(
										recommendedSleipner['averageTravellingSpeedOnTracks(km/h)'].replace(
											/,/g,
											'.',
										),
									) +
								' km'
							}
							styleProps={{marginTop: -4}}
						/>
					</RowItem>
				</Row>

				<Hr />

				<Row>
					<RowItem>
						<ValueTitle>Time savings</ValueTitle>
						<TextLineWithValue
							text="Annual time spent travelling with Sleipner"
							value={
								calculatedValues['Annual time spent travelling with Sleipner (%)'] + '%'
							}
						/>
						<TextLineWithValue
							text="Annual saved travelling time that can be transferred into production"
							value={calculatedValues['Annual saved travelling time (h)'] + ' h'}
						/>
					</RowItem>
				</Row>

				<Hr />

				<Row>
					<RowItem>
						<ValueTitle>Utilisation</ValueTitle>
						<MultiLineText width={'160px'}>
							During normal operations at the mine, excavators spend up to 15% of their
							working time on tracks, resulting in a negative impact on the efficiency of
							operations in the pit.
						</MultiLineText>
					</RowItem>

					<RowItem styleProps={{position: 'relative', left: -90, top: -20}}>
						<ValueTitle> </ValueTitle>

						<TextValuePill
							text="Without Sleipner"
							value={calculatedValues['Utilization without Sleipner (%)'] + '%'}
						></TextValuePill>

						<Image
							src={charts.chartUtilizationWithout}
							style={{width: 65, height: 65, marginTop: 5}}
						></Image>

						<View style={{position: 'relative', left: 85, top: -50}}>
							<LegendItem
								color="black"
								text={
									'Travelling ' +
									calculatedValues['Annual time spent travelling without Sleipner (%)'] +
									'%'
								}
							></LegendItem>
							<LegendItem
								color="#666"
								text={
									'Digging ' +
									(100 -
										calculatedValues[
											'Annual time spent travelling without Sleipner (%)'
										]) +
									'%'
								}
							></LegendItem>
						</View>

						<Image
							src={rightArrow}
							style={{width: 20, height: 20, position: 'relative', left: 163, top: 53}}
						></Image>
					</RowItem>
				</Row>

				<Row styleProps={{marginTop: -30}}>
					<RowItem>
						<MultiLineText width={'160px'}>
							With Sleipner, travelling time is reduced by up to 85% and the saved time
							can be used for digging. This has a direct benefit for production.
						</MultiLineText>
					</RowItem>

					<RowItem styleProps={{position: 'relative', left: -90, top: -20}}>
						<TextValuePill
							text="With Sleipner"
							value={calculatedValues['Utilization with Sleipner (%)'] + '%'}
						></TextValuePill>

						<Image
							src={charts.chartUtilizationWithSleipnerSavings}
							style={{width: 65, height: 65, marginTop: 10, transform: 'rotate(-80deg)'}}
						></Image>

						<View style={{position: 'relative', left: 75, top: -60}}>
							<LegendItem
								color="#fff"
								text={
									'Saved Time ' +
									(calculatedValues['Annual time spent travelling without Sleipner (%)'] -
										calculatedValues['Annual time spent travelling with Sleipner (%)']) +
									'%'
								}
							></LegendItem>
							<LegendItem
								color="black"
								text={
									'Travelling ' +
									calculatedValues['Annual time spent travelling with Sleipner (%)'] +
									'%'
								}
							></LegendItem>
							<LegendItem
								color="#666"
								text={
									'Digging ' +
									(100 -
										calculatedValues['Annual time spent travelling with Sleipner (%)']) +
									'%'
								}
							></LegendItem>
						</View>

						<View style={{position: 'relative', top: -125, left: 190}}>
							<Image
								src={charts.chartUtilizationWithSleipner}
								style={{width: 65, height: 65}}
							></Image>
							<View style={{position: 'relative', left: 75, top: -50}}>
								<LegendItem
									color="black"
									text={
										'Travelling ' +
										calculatedValues['Annual time spent travelling with Sleipner (%)'] +
										'%'
									}
								></LegendItem>
								<LegendItem
									color="#666"
									text={
										'Digging ' +
										(100 -
											calculatedValues[
												'Annual time spent travelling with Sleipner (%)'
											]) +
										'%'
									}
								></LegendItem>
							</View>
						</View>
					</RowItem>
				</Row>

				<Hr styleProps={{marginTop: -165}}></Hr>

				<Row>
					<RowItem styleProps={{paddingRight: 5}}>
						<ValueTitle>Production Increase</ValueTitle>
						<HighlightBox>
							<Text>Annual increase in production</Text>
							<ValueLarge>
								{baseFormatClean(
									calculatedValues['Annual increase in production (tonnes)'],
								) + ' tonnes'}
							</ValueLarge>
						</HighlightBox>
					</RowItem>
					<RowItem styleProps={{paddingLeft: 5}}>
						<ValueTitle> </ValueTitle>
						<HighlightBox>
							<Text>Annual production increase</Text>
							<ValueLarge>
								{formatCurrency(calculatedValues['Production increase ($)'])}
							</ValueLarge>
						</HighlightBox>
					</RowItem>
				</Row>

				<Row styleProps={{marginTop: 15}}>
					<MultiLineText width={'100%'}>
						In addition to the production increase from time saved during transportation,
						the Sleipner dollies makes it possible to maintain a steady production rate
						that is not affected by major undercarriage rebuilds every other year.
					</MultiLineText>
				</Row>
			</View>
		</PageWithHeaderAndFooter>
	);
};
